import * as m from "@paraglide/messages"
import { PlaydataButtonAnchor, PlaydataSection } from "@playdata/PlaydataPrimitives"
import { usePlaydataSampleReportUrl } from "@playdata/playdata-settings-context"
import type { SupportedLocale } from "@site-types/content"
import { createFileRoute, Link } from "@tanstack/react-router"
import { playdataRouteHead } from "@utils/playdata-route-head"

export const Route = createFileRoute("/$locale/playdata/about")({
  head: ({ params }) => {
    const locale = params.locale as SupportedLocale
    return playdataRouteHead({
      description: m.playdata_about_meta_description({}, { locale }),
      locale,
      path: "/playdata/about",
      title: m.playdata_about_meta_title({}, { locale }),
    })
  },
  component: RouteComponent,
})

/** The live page is a single centred column of prose with bold run-in headings. */
function RouteComponent() {
  const { locale: raw } = Route.useParams()
  const locale = raw as SupportedLocale
  const sampleReportUrl = usePlaydataSampleReportUrl(locale)

  const coverage = [
    m.playdata_about_coverage_item1({}, { locale }),
    m.playdata_about_coverage_item2({}, { locale }),
    m.playdata_about_coverage_item3({}, { locale }),
    m.playdata_about_coverage_item4({}, { locale }),
  ]

  return (
    <PlaydataSection>
      <h1 className="mb-[4rem] text-center text-[2.8rem] md:text-[3.4rem]">
        <strong>{m.playdata_about_heading({}, { locale })}</strong>
      </h1>

      <div className="mx-auto flex max-w-[66ch] flex-col gap-[2.4rem] text-[1.5rem] leading-[1.8]">
        <p>{m.playdata_about_lead({}, { locale })}</p>
        {/*
         * The company name links back to PLAYBRAIN, as it does on the live
         * about page. Only the name is the link, so the sentence is built
         * from three messages rather than one — Japanese puts the name in a
         * different position, and a single string could not carry the anchor
         * for both.
         */}
        <p>
          {m.playdata_about_operatedBy_before({}, { locale })}
          <Link
            className="underline underline-offset-[0.2em]"
            params={{ locale }}
            to="/$locale"
          >
            {m.playdata_about_operatedBy_link({}, { locale })}
          </Link>
          {m.playdata_about_operatedBy_after({}, { locale })}
        </p>

        <h2 className="mt-[1.6rem] text-[1.9rem]">
          <strong>{m.playdata_about_coverage_heading({}, { locale })}</strong>
        </h2>
        <p>{m.playdata_about_coverage_intro({}, { locale })}</p>
        <ol className="flex list-none flex-col gap-[0.4rem] p-0">
          {coverage.map((item, index) => (
            <li key={item}>
              {index + 1}．{item}
            </li>
          ))}
        </ol>
        {/* Live marks the note with * in English and ※ in Japanese. */}
        <p>
          {locale === "ja" ? "※" : "* "}
          {m.playdata_about_coverage_note({}, { locale })}
        </p>

        {/*
          Japanese only, as on the live site: the English About page stops after
          the coverage list and goes straight to the sample report.
        */}
        {locale === "ja" ? (
          <>
            <h2 className="mt-[1.6rem] text-[1.9rem]">
              <strong>{m.playdata_about_method_heading({}, { locale })}</strong>
            </h2>
            <ul className="flex list-none flex-col gap-[0.4rem] p-0">
              <li>・{m.playdata_about_method_item1({}, { locale })}</li>
              <li>・{m.playdata_about_method_item2({}, { locale })}</li>
            </ul>
            <p>※{m.playdata_about_method_note({}, { locale })}</p>

            <h2 className="mt-[1.6rem] text-[1.9rem]">
              <strong>{m.playdata_about_fields_heading({}, { locale })}</strong>
            </h2>
            <ul className="flex list-none flex-col gap-[0.4rem] p-0">
              <li>・{m.playdata_about_fields_item1({}, { locale })}</li>
              <li>・{m.playdata_about_fields_item2({}, { locale })}</li>
            </ul>
          </>
        ) : null}

        <p className="mt-[1.6rem]">{m.playdata_about_sampleIntro({}, { locale })}</p>
        <div className="flex justify-center">
          <PlaydataButtonAnchor
            href={sampleReportUrl}
            rel="noopener"
            target="_blank"
          >
            {m.playdata_about_sampleCta({}, { locale })}
          </PlaydataButtonAnchor>
        </div>
      </div>
    </PlaydataSection>
  )
}
